import { authApi } from './auth'

export interface ApiUserWithTeams {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  storeIds: string[]
  admin: boolean
  isActive: boolean
  teams: Array<{
    id: number
    name: string
    type: string
  }>
  createdAt: string
  updatedAt: string
}

/** Decode a JWT payload without verifying signature (client-side only). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1]
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

export const usersApi = {
  /**
   * Compose the FE's "me" view from the authoritative GET /auth/profile +
   * the JWT's `isAdmin` claim.
   *
   * Why both? `/auth/profile` is authoritative for everything except
   * `admin` — the User entity declares `admin` as `hidden: true`, so the
   * serializer strips it. The JWT carries `isAdmin` as a claim, so we
   * decode the token just for that one field.
   */
  getMe: async (): Promise<ApiUserWithTeams> => {
    const token = localStorage.getItem('etx_access_token')
    if (!token) throw new Error('No access token')

    const profile = await authApi.getProfile()
    const admin = (decodeJwtPayload(token)?.isAdmin as boolean) ?? false

    return {
      id: profile.id,
      email: profile.email,
      firstName: profile.firstName,
      lastName: profile.lastName,
      storeIds: profile.teams.map((t) => String(t.id)),
      admin,
      isActive: profile.isActive,
      teams: profile.teams.map((t) => ({ id: t.id, name: t.name, type: t.type })),
      createdAt: profile.createdAt,
      updatedAt: profile.updatedAt,
    }
  },

  /** One-shot fetch used by /welcome/profile to pre-fill the name fields with
   *  whatever the inviter set on the auto-created user.
   *
   *  Reads the authoritative GET /v1/auth/profile. This previously called
   *  GET /v1/users/me — an endpoint the BE does not have (there is no
   *  /v1/users/* at all); it existed only in the mock. Because the caller
   *  swallows the error, it 404'd *silently* in production and the pre-fill
   *  never happened, while working fine locally against the mock. */
  fetchProfile: async () => {
    const me = await authApi.getProfile()
    return { firstName: me.firstName, lastName: me.lastName, email: me.email }
  },
}
